import { useCallback } from 'react';
import { NotificationSignalRContext } from './NotificationSignalRContext';
import { useSettingsWithManage } from '../../manage-config';
import {
  getIsActive,
  notificationHubUrl,
  buildUserGroup,
  buildBroadcastGroup
} from '../model/utils';
import {
  MODULE_NAME,
  HUB_EVENT_NOTIFICATION_ADDED,
  HUB_METHOD_JOIN_GROUP,
  NOTIFICATION_ADDED_EVENT
} from '../model/constants';
import { emptyNull } from '../../../shared/misc';
import { getUserId } from '../../manage-config/model/utils';

// Provider for the real-time notification hub. Gated on this module's own
// `isActive`, so it is independent of the single-login SignalR module. Hooks
// run unconditionally; the connection only mounts once all required parameters
// are available. On open it joins the user-specific and broadcast groups;
// received notifications are re-broadcast as a `notification-added` window
// event consumed by `NotificationToast` (Vue parity).
export const NotificationSignalRProvider = ({ children }) => {
  const { instance, eventShort } = useSettingsWithManage(MODULE_NAME) ?? emptyNull;
  const userId = getUserId();
  const isActive = getIsActive();
  const hubUrl = notificationHubUrl();

  const userGroup = userId ? buildUserGroup({ instance, eventShort, userId }) : null;
  const broadcastGroup = instance && eventShort ? buildBroadcastGroup({ instance, eventShort }) : null;

  const connectEnabled = Boolean(isActive && hubUrl && (userGroup || broadcastGroup));

  const handleOpen = useCallback(
    (connection) => {
      [userGroup, broadcastGroup].filter(Boolean).forEach((group) => {
        connection.invoke(HUB_METHOD_JOIN_GROUP, group).catch((err) => {
          console.error('Failed to join notification group', group, err);
        });
      });
    },
    [userGroup, broadcastGroup]
  );

  NotificationSignalRContext.useSignalREffect(
    HUB_EVENT_NOTIFICATION_ADDED,
    (payload) => {
      // The hub sends the notification as a JSON-encoded string argument.
      let notification = payload;
      if (typeof payload === 'string') {
        try {
          notification = JSON.parse(payload);
        } catch (err) {
          console.error('Failed to parse notification payload', payload, err);
          return;
        }
      }
      window.dispatchEvent(new CustomEvent(NOTIFICATION_ADDED_EVENT, { detail: { notification } }));
    },
    []
  );

  if (!connectEnabled) return children;

  return (
    <NotificationSignalRContext.Provider
      connectEnabled={connectEnabled}
      dependencies={[hubUrl, userGroup, broadcastGroup]}
      url={hubUrl}
      withCredentials={false}
      automaticReconnect
      onOpen={handleOpen}
    >
      {children}
    </NotificationSignalRContext.Provider>
  );
};
